import React, { PropsWithChildren, useEffect, useState } from 'react'
import {
  DropDownListBlurEvent,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import DropdownOption from './DropdownOption';
import SearchableDropdown from './SearchableDropdown';
import { CompanyService } from '../../services';
import { CompanyModel } from '../../data/entities';

interface SearchableCompanyDropdownProps extends PropsWithChildren {
  value?: any;
  name: string;
  isInvalid?: boolean;
  onBlur?: (e: DropDownListBlurEvent) => void;
  onChange?: (e: DropDownListChangeEvent) => void;
  onSelect?: (value: DropdownOption) => void;
}

export const SearchableCompanyDropdown = React.forwardRef(
  (
    props: Readonly<SearchableCompanyDropdownProps>,
    ref: React.ForwardedRef<HTMLSelectElement | null>
  ) => {
    const [options, setOptions] = useState<DropdownOption[]>([]);

    const sort = (data: CompanyModel[]): CompanyModel[] => {
      // Sort tubings by company name and then by tubing name
      return data.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA !== nameB) {
          return nameA! < nameB! ? -1 : 1;
        }

        // Secondary sort by tubing name if company names are equal
        const companyNameA = a.name!.toUpperCase();
        const companyNameB = b.name!.toUpperCase();
        return companyNameA < companyNameB ? -1 : companyNameA > companyNameB ? 1 : 0;
      });
    };

    useEffect(() => {
      const fetchData = async () => {
        let companies = await new CompanyService().getAllCompanies();

        companies = sort(companies);
        const newOptions = companies.map<DropdownOption>((company: CompanyModel) => ({
          value: company.id?.toString() ?? "",
          text: `${company.name}`,
        }));

        setOptions(newOptions);
      };

      fetchData();
    }, []);

    const getValue = () => {
      if (props.value) {
        return options.find((option) => option.value === props.value?.toString());
      }
    }

    return (
      <SearchableDropdown
        label="Company"
        name={props.name}
        options={options}
        value={getValue()}
        onChange={props.onChange}
        onSelect={props.onSelect}
        onBlur={props.onBlur}
        isInvalid={props.isInvalid}
      >
        {props.children}
      </SearchableDropdown>
    );
  }
);

export default SearchableCompanyDropdown
